import React from 'react';
import {
  ArrowDownTrayIcon, ArrowUpTrayIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import PermissionsContainer from '../../components/PermissionsContainer';

/**
 * The "Sage Uploads & Exports" tile — V1's seven Sage screens as seven actions.
 *
 * The tile is only the list; each action renders into the dashboard's shared results
 * pane (drawer or full page, per the user's Drawer-view setting), the same way the
 * Accrual and Invoice Search tiles work. Confirm in particular needs the room: it is a
 * grid plus a detail view.
 */
export type SageActionKey =
  | 'upload-billtos' | 'upload-vendors'
  | 'export-invoices' | 'export-driverpay' | 'export-rebills' | 'export-addlcost'
  | 'confirm';

export interface SageAction {
  key: SageActionKey;
  label: string;
  /** Shown as the results pane's title. */
  title: string;
  group: 'Uploads' | 'Exports' | 'Confirm';
  /** Each action is gated on its own. These move money into Sage — uploading bill-tos is
   *  not the same authority as exporting a payroll run — so they default to HIDDEN and an
   *  admin grants each one. */
  permission: string;
}

export const SAGE_ACTIONS: SageAction[] = [
  { key: 'upload-billtos', permission: 'sage-upload-billtos', label: 'Upload Sage Bill Tos', title: 'Upload Sage Bill Tos', group: 'Uploads' },
  { key: 'upload-vendors', permission: 'sage-upload-vendors', label: 'Upload Sage Vendors', title: 'Upload Sage Vendors', group: 'Uploads' },
  { key: 'export-invoices', permission: 'sage-export-invoices', label: 'Export Invoices to Sage', title: 'Export Invoices to Sage', group: 'Exports' },
  { key: 'export-driverpay', permission: 'sage-export-driverpay', label: 'Export Driver Pay to Sage', title: 'Export Driver Pay to Sage', group: 'Exports' },
  { key: 'export-rebills', permission: 'sage-export-rebills', label: 'Export Rebills/Credits to Sage', title: 'Export Rebills/Credits to Sage', group: 'Exports' },
  { key: 'export-addlcost', permission: 'sage-export-addlcost', label: 'Export Addl Cost to Sage', title: 'Export Additional Cost to Sage', group: 'Exports' },
  { key: 'confirm', permission: 'sage-confirm', label: 'Confirm Export to Sage', title: 'Confirm Export to Sage', group: 'Confirm' },
];

const GROUP_ICON = {
  Uploads: ArrowUpTrayIcon,
  Exports: ArrowDownTrayIcon,
  Confirm: CheckCircleIcon,
} as const;

const ROW = 'w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded '
  + 'hover:bg-theme-bg-tertiary text-label focus:outline-none focus:ring-1 focus:ring-blue-500';

interface Props {
  selected: SageActionKey | null;
  onSelect: (action: SageAction) => void;
}

const SagePanel: React.FC<Props> = ({ selected, onSelect }) => {
  let lastGroup: SageAction['group'] | null = null;
  return (
    <div className="p-2 space-y-0.5">
      {SAGE_ACTIONS.map((action) => {
        const Icon = GROUP_ICON[action.group];
        const newGroup = action.group !== lastGroup;
        lastGroup = action.group;
        return (
          <React.Fragment key={action.key}>
            {newGroup && (
              <div className="px-2 pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
                {action.group}
              </div>
            )}
            <PermissionsContainer componentId={action.permission} componentName={action.label} defaultHidden>
              <button
                type="button"
                className={`${ROW} ${selected === action.key ? 'bg-theme-bg-tertiary font-medium' : ''}`}
                onClick={() => onSelect(action)}
              >
                <Icon className="h-4 w-4 flex-shrink-0 text-muted" aria-hidden="true" />
                <span>{action.label}</span>
              </button>
            </PermissionsContainer>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SagePanel;
